import { useMemo, useState } from 'react';
import { useQueryClient, useMutation } from 'react-query';
import { useFirebase } from 'components/Firebase/index.js';
import { useOrgId } from 'hooks/index.js';
import { vodUrl } from '../../../client-config.js';
import useVodProductsQuery from 'hooks/useVodProductsQuery.js';
import { isEqual } from 'helpers/isEqual.js';

class AddProductError extends Error {
  constructor(message, errorResponse) {
    super(message);
    this.name = 'AddProductError';
    this.errorResponse = errorResponse;
  }
}

const isSameProduct = (productToAdd, existingProduct) => {
  if (!productToAdd || !existingProduct) return false;
  const { data, title, sku, sourceUrl } = existingProduct;
  return isEqual(productToAdd, { data, title, sku, sourceUrl });
};

const useAddVodProductMutation = (options = {}) => {
  const [productToAdd, setProductToAdd] = useState(null);
  const firebase = useFirebase();
  const orgId = useOrgId();
  const queryClient = useQueryClient();
  const fetchUrl = new URL(`${vodUrl}/v1/orgs/${orgId}/products`);
  const { onSuccess, onSettled, onError, ...rest } = options || {};

  const queryKey = useMemo(() => ['vodProducts', orgId], [orgId]);

  useVodProductsQuery({
    search: productToAdd?.sku,
    searchFields: [ 'sku' ],
    pageSize: 1,
  }, {
    onSuccess: (products) => {
      if (isSameProduct(productToAdd, products.data[0])) {
        onSuccess({
          data: products.data[0],
        });
      } else {
        onError(new AddProductError('Failed to add duplicate product', {
          httpStatus: 409,
        }));
      }
    },
    onSettled: (products, error) => {
      setProductToAdd(null);
      if (error) return;
      onSettled({
        data: products.data[0],
      });
    },
    enabled: !!productToAdd?.sku,
  });

  return useMutation({
    mutationFn: async data => {
      const idToken = await firebase.getIdToken();
      const {
        productReference: sku,
        publicUrl: sourceUrl,
        thumbnail,
        brand,
        title,
        price,
        discountedPrice,
        currency,
        opts = {},
      } = data;
      const productData = {
        sku,
        sourceUrl,
        data: {
          thumbnail,
          brand,
          price: {
            original: price ? parseFloat(price) : null,
            amount: discountedPrice ? parseFloat(discountedPrice) : null,
            currency: currency || null,
          },
        },
        title,
      };

      const { withPlatformEvent = true } = opts;
      const queryParams = new URLSearchParams();
      if (!withPlatformEvent) queryParams.set('withPlatformEvent', false);
      const queryParamsString = queryParams.size > 0 ? `?${queryParams.toString()}` : '';

      const response = await fetch(`${fetchUrl}${queryParamsString}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        mode: 'cors',
        body: JSON.stringify({
          data: productData,
        }),
      });
      if (response.ok) {
        // Product was added
        const res = await response.json();
        return res;
      } else {
        const errorResponse = await response.json();
        if (errorResponse.httpStatus === 409) {
          const { data: cachedProduct = {} } = queryClient.getQueryData(['vodProducts', orgId, {
            search: sku,
            pageSize: 1,
            searchFields: ['sku'],
          }]) || {};
          if (cachedProduct?.length) {
            if (isSameProduct(productData, cachedProduct[0])) {
              return {
                data: cachedProduct[0],
              };
            }
          } else {
            // Set product data which will trigger the search for the original product
            setProductToAdd(productData);
            return false;
          }
        }
        throw new AddProductError(`Failed to add the product ${errorResponse.errors.pop().message}`, errorResponse);
      }
    },
    onSuccess: (data, variables, context) => {
      if (onSuccess) {
        onSuccess(data, variables, context);
      }
      return queryClient.setQueryData(queryKey, (products = {}) => {
        products.data = products.data || [];
        products.data.push(data.data);
        return products;
      });
    },
    onSettled: (data, error, variables, context) => {
      if (onSettled) {
        onSettled(data, error, variables, context);
      }
      if (!error) {
        return queryClient.invalidateQueries(['vodProducts', orgId, {}]);
      }
    },
    ...rest,
  });
};

export default useAddVodProductMutation;
